import type { ChatMessage, ReceiveData } from '@/api/ai/aiType';
import type { Response as WrongbookItem } from '@/api/do-question/mistakerecordApi';
import { aiApi } from '@/api/ai/aiApi';
import { AI_SOURCE_MAIN } from '@/api/ai/sources';
import { fetchWrongbookApi } from '@/api/do-question/mistakerecordApi';

export type StudyCoachEvent = 'subject_entered' | 'wrongbook_loaded' | 'review_completed' | 'user_requested_help';
export type StudyCoachActionType = 'review_question' | 'open_wrongbook' | 'show_more' | 'dismiss' | 'chat';

export interface StudyCoachAction {
  type: StudyCoachActionType;
  label: string;
  questionId?: number;
}

export interface StudyCoachSuggestion {
  message: string;
  reason?: string;
  priority: 'low' | 'normal' | 'high';
  actions: StudyCoachAction[];
  sourceEvent: StudyCoachEvent;
}

export interface StudyCoachContext {
  subjectId: string;
  subjectName: string;
  wrongbook: WrongbookItem[];
  event: StudyCoachEvent;
}

const SUGGESTION_STORAGE_PREFIX = 'probfun:study-coach:v1:';
const SUGGESTION_TTL_MS = 24 * 60 * 60 * 1000;
const RETRY_COOLDOWN_MS = 5 * 60 * 1000;
const WRONGBOOK_CACHE_TTL_MS = 60 * 1000;

interface PersistedStudyCoachSuggestion {
  version: 1;
  generatedAt: number;
  lastAttemptAt: number;
  fingerprint: string;
  suggestion: StudyCoachSuggestion;
}

export interface StudyCoachSuggestionResult {
  suggestion: StudyCoachSuggestion;
  source: 'generated' | 'cached' | 'fallback';
  retryAt?: number;
}

const conversations = new Map<string, string>();
const inFlightSuggestions = new Map<string, Promise<StudyCoachSuggestionResult>>();
const suggestionMemoryCache = new Map<string, PersistedStudyCoachSuggestion>();
const wrongbookCache = new Map<string, {
  entries?: WrongbookItem[];
  expiresAt: number;
  request?: Promise<WrongbookItem[]>;
  invalidateOnResolve?: boolean;
  removedQuestionIds?: Set<number>;
  error?: unknown;
}>();

function wrongbookFingerprint(wrongbook: WrongbookItem[]) {
  return [...wrongbook]
    .sort((a, b) => a.questionId - b.questionId)
    .map(item => [item.questionId, item.chapterName, item.errorCount, item.firstErrorAt, item.questionContent].join(':'))
    .join('|');
}

function normalizeSubjectName(name: unknown) {
  return String(name ?? '').replace(/[\s（）()]/g, '');
}

export function filterStudyCoachWrongbook(
  wrongbook: WrongbookItem[],
  subject: Pick<StudyCoachContext, 'subjectId' | 'subjectName'>,
) {
  const normalizedName = normalizeSubjectName(subject.subjectName);
  return wrongbook.filter((item) => {
    if (item.subjectId !== undefined && item.subjectId !== null)
      return String(item.subjectId) === subject.subjectId;
    return normalizeSubjectName(item.subjectName) === normalizedName;
  });
}

function parseSuggestion(text: string, context: StudyCoachContext): StudyCoachSuggestion | null {
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end <= start)
    return null;
  try {
    const parsed = JSON.parse(text.slice(start, end + 1)) as Partial<StudyCoachSuggestion>;
    const validIds = new Set(context.wrongbook.map(item => item.questionId));
    const actions = Array.isArray(parsed.actions)
      ? parsed.actions.filter((action): action is StudyCoachAction => {
          if (!action || typeof action !== 'object' || typeof action.type !== 'string'
            || !['review_question', 'open_wrongbook', 'show_more', 'dismiss', 'chat'].includes(action.type)) {
            return false;
          }
          if (action.type === 'review_question') {
            const questionId = Number(action.questionId);
            return Number.isFinite(questionId) && validIds.has(questionId);
          }
          return action.questionId === undefined || validIds.has(Number(action.questionId));
        })
          .map(action => ({ ...action, questionId: action.questionId ? Number(action.questionId) : undefined }))
      : [];
    if (typeof parsed.message !== 'string' || !parsed.message.trim() || !actions.length)
      return null;
    return {
      message: parsed.message.trim(),
      reason: typeof parsed.reason === 'string' ? parsed.reason : undefined,
      priority: parsed.priority === 'high' || parsed.priority === 'low' ? parsed.priority : 'normal',
      actions,
      sourceEvent: context.event,
    };
  }
  catch {
    return null;
  }
}

export function buildFallbackSuggestion(context: StudyCoachContext): StudyCoachSuggestion {
  const actions: StudyCoachAction[] = context.wrongbook.length
    ? [
        {
          type: 'review_question',
          label: '复习一道错题',
          questionId: [...context.wrongbook].sort((a, b) => (b.errorCount ?? 0) - (a.errorCount ?? 0))[0].questionId,
        },
        { type: 'open_wrongbook', label: '打开错题本' },
      ]
    : [{ type: 'open_wrongbook', label: '打开错题本' }];

  const chapters = [...new Set(context.wrongbook.map(item => item.chapterName).filter(Boolean))].slice(0, 3);
  const mostRepeated = [...context.wrongbook].sort((a, b) => (b.errorCount ?? 0) - (a.errorCount ?? 0))[0];
  const chapterText = chapters.length ? `目前主要集中在${chapters.join('、')}。` : '章节信息还不完整。';
  const repeatText = mostRepeated?.errorCount && mostRepeated.errorCount > 1
    ? `其中有一道题已经错了 ${mostRepeated.errorCount} 次，适合先回看错因，再独立重做。`
    : '建议先回看最近留下的题目，写下卡住的步骤后再重新作答。';
  return {
    message: context.wrongbook.length
      ? `${context.subjectName}错题本中有 ${context.wrongbook.length} 道错题。根据你当前留下的记录，${chapterText}${repeatText}我先为你选出最值得复习的一道，完成后再根据结果调整下一步。`
      : `${context.subjectName}暂未检测到错题记录。你可以先打开错题本确认范围；之后我会结合你新留下的章节、错误次数和复习结果，帮你安排更具体的下一步。`,
    priority: 'normal',
    actions,
    sourceEvent: context.event,
  };
}

async function generate(context: StudyCoachContext, conversationId?: string): Promise<{ suggestion: StudyCoachSuggestion; conversationId?: string }> {
  const topQuestions = [...context.wrongbook]
    .sort((a, b) => (b.errorCount ?? 0) - (a.errorCount ?? 0))
    .slice(0, 5)
    .map(item => ({
      questionId: item.questionId,
      chapterName: item.chapterName,
      errorCount: item.errorCount,
      firstErrorAt: item.firstErrorAt,
      preview: item.questionContent.slice(0, 120),
    }));
  const system: ChatMessage = {
    role: 'system',
    content: '你是主动、具体且温和的学习教练。只能引用用户提供的事实，不得编造学习记录、完成情况或掌握程度。请根据错题章节、错误次数和首次出错时间，给出可执行的复习建议；这些只能作为有限线索，不能下结论说用户已经掌握或一定薄弱。message 必须使用清晰的 Markdown 排版，严格按以下结构输出：\n### 复习判断\n- 1 条，说明根据错题本推断的近期内容，并注明“根据错题本推断”。\n### 建议步骤\n- 2-3 条，每条以动词开头，包含具体动作。\n### 下一步\n- 1 条，明确推荐复习哪道真实错题或打开错题本。\n每条不超过 35 个汉字，总字数控制在 100-180 字；不要输出连续的大段文字，不要添加问候、空泛鼓励或其他小节。无错题时仍按结构输出，并如实说明暂无记录。只返回 JSON：{message,reason,priority,actions}。actions 类型只能是 review_question/open_wrongbook/show_more/dismiss/chat，review_question 必须带 questionId。',
  };
  const user: ChatMessage = {
    role: 'user',
    content: JSON.stringify({ task: '结合可用的错题历史，概括用户近期复习内容并生成具体的复习计划', subject: { id: context.subjectId, name: context.subjectName }, event: context.event, wrongbookCount: context.wrongbook.length, topQuestions }),
  };
  let text = '';
  let nextConversationId = conversationId;
  await new Promise<void>((resolve, reject) => {
    aiApi({ messages: [system, user], source: AI_SOURCE_MAIN, ...(conversationId ? { conversationId } : {}) }, () => {}, (data: ReceiveData) => {
      if (data.type === 'text')
        text += data.content || '';
      if (data.type === 'conversation_info' && data.content)
        nextConversationId = data.content;
    }, result => result?.hasValidText ? resolve() : reject(new Error('AI response unavailable')));
  });
  const suggestion = parseSuggestion(text, context);
  if (!suggestion) {
    console.warn('[study-coach] invalid model suggestion; using context fallback', { responseLength: text.length });
    return { suggestion: buildFallbackSuggestion(context), conversationId: nextConversationId };
  }
  return { suggestion, conversationId: nextConversationId };
}

export function loadStudyCoachWrongbook(username: string): Promise<WrongbookItem[]> {
  const key = username.trim();
  if (!key)
    return Promise.resolve([]);

  const now = Date.now();
  const cached = wrongbookCache.get(key);
  if (cached?.entries && cached.expiresAt > now)
    return Promise.resolve(cached.entries);
  if (cached?.request)
    return cached.request;
  if (cached?.error && cached.expiresAt > now)
    return Promise.reject(cached.error);

  const requestEntry: { entries?: WrongbookItem[]; expiresAt: number; request?: Promise<WrongbookItem[]>; invalidateOnResolve?: boolean; removedQuestionIds?: Set<number>; error?: unknown } = { expiresAt: now };
  const request = fetchWrongbookApi(key).then((data: any) => {
    const entries = Array.isArray(data) ? data : data?.wrongbook ?? data?.items ?? [];
    let result = Array.isArray(entries) ? entries as WrongbookItem[] : [];
    if (requestEntry.removedQuestionIds?.size) {
      result = result.filter(item => !requestEntry.removedQuestionIds?.has(item.questionId));
    }
    if (requestEntry.invalidateOnResolve) {
      wrongbookCache.delete(key);
    }
    else {
      wrongbookCache.set(key, { entries: result, expiresAt: Date.now() + WRONGBOOK_CACHE_TTL_MS });
    }
    return result;
  }).catch((error) => {
    wrongbookCache.set(key, { expiresAt: Date.now() + WRONGBOOK_CACHE_TTL_MS, error });
    throw error;
  });
  requestEntry.request = request;
  wrongbookCache.set(key, requestEntry);
  return request;
}

export function invalidateStudyCoachWrongbook(username: string, questionId?: number) {
  const key = username.trim();
  const cached = wrongbookCache.get(key);
  if (cached?.request) {
    cached.invalidateOnResolve = true;
    if (questionId !== undefined) {
      cached.removedQuestionIds ??= new Set();
      cached.removedQuestionIds.add(questionId);
    }
  }
  else {
    wrongbookCache.delete(key);
  }
}

export function getStudyCoachWrongbookRetryAt(username: string) {
  const cached = wrongbookCache.get(username.trim());
  return cached?.error && cached.expiresAt > Date.now() ? cached.expiresAt : undefined;
}

function suggestionStorageKey(username: string, subjectId: string) {
  return `${SUGGESTION_STORAGE_PREFIX}${encodeURIComponent(username)}:${encodeURIComponent(subjectId)}`;
}

function readPersistedSuggestion(username: string, subjectId: string): PersistedStudyCoachSuggestion | null {
  try {
    const raw = localStorage.getItem(suggestionStorageKey(username, subjectId));
    if (!raw)
      return null;
    const value = JSON.parse(raw) as PersistedStudyCoachSuggestion;
    if (value.version !== 1 || !Number.isFinite(value.generatedAt) || !Number.isFinite(value.lastAttemptAt)
      || typeof value.fingerprint !== 'string' || !value.suggestion || typeof value.suggestion.message !== 'string'
      || !Array.isArray(value.suggestion.actions)) {
      return null;
    }
    return value;
  }
  catch {
    return null;
  }
}

function writePersistedSuggestion(username: string, subjectId: string, value: PersistedStudyCoachSuggestion) {
  try {
    localStorage.setItem(suggestionStorageKey(username, subjectId), JSON.stringify(value));
  }
  catch {
    // Storage may be unavailable or full; in-flight de-duplication still applies.
  }
}

export async function getStudyCoachSuggestion(context: StudyCoachContext, username: string): Promise<StudyCoachSuggestionResult> {
  const userKey = username.trim();
  const fallback = () => buildFallbackSuggestion(context);
  if (!userKey)
    return { suggestion: fallback(), source: 'fallback' };

  const inFlightKey = `${userKey}:${context.subjectId}`;
  const key = suggestionStorageKey(userKey, context.subjectId);
  const fingerprint = wrongbookFingerprint(context.wrongbook);
  const now = Date.now();
  const stored = readPersistedSuggestion(userKey, context.subjectId) ?? suggestionMemoryCache.get(key) ?? null;
  const age = stored ? now - stored.generatedAt : Number.POSITIVE_INFINITY;

  if (stored && age >= 0 && age < SUGGESTION_TTL_MS) {
    if (stored.fingerprint === fingerprint)
      return { suggestion: stored.suggestion, source: 'cached' };
    return { suggestion: fallback(), source: 'fallback' };
  }

  const inFlight = inFlightSuggestions.get(inFlightKey);
  if (inFlight)
    return inFlight;

  const retryAt = stored ? stored.lastAttemptAt + RETRY_COOLDOWN_MS : 0;
  if (retryAt > now)
    return { suggestion: fallback(), source: 'fallback', retryAt };

  const startedAt = now;
  const attempt: PersistedStudyCoachSuggestion = {
    version: 1,
    generatedAt: stored?.generatedAt ?? 0,
    lastAttemptAt: startedAt,
    fingerprint: stored?.fingerprint ?? '',
    suggestion: stored?.suggestion ?? fallback(),
  };
  suggestionMemoryCache.set(key, attempt);
  writePersistedSuggestion(userKey, context.subjectId, attempt);

  const suggestionPromise = generate(context, conversations.get(inFlightKey)).then((result) => {
    if (result.conversationId)
      conversations.set(inFlightKey, result.conversationId);
    const generatedAt = Date.now();
    const record: PersistedStudyCoachSuggestion = {
      version: 1,
      generatedAt,
      lastAttemptAt: generatedAt,
      fingerprint,
      suggestion: result.suggestion,
    };
    suggestionMemoryCache.set(key, record);
    writePersistedSuggestion(userKey, context.subjectId, record);
    return { suggestion: result.suggestion, source: 'generated' as const };
  }).catch((error) => {
    const lastAttemptAt = Date.now();
    const record: PersistedStudyCoachSuggestion = {
      version: 1,
      generatedAt: stored?.generatedAt ?? 0,
      lastAttemptAt,
      fingerprint: stored?.fingerprint ?? '',
      suggestion: stored?.suggestion ?? fallback(),
    };
    suggestionMemoryCache.set(key, record);
    writePersistedSuggestion(userKey, context.subjectId, record);
    console.warn('[study-coach] AI suggestion failed; using current wrongbook facts', error);
    return {
      suggestion: fallback(),
      source: 'fallback' as const,
      retryAt: lastAttemptAt + RETRY_COOLDOWN_MS,
    };
  }).finally(() => {
    if (inFlightSuggestions.get(inFlightKey) === suggestionPromise)
      inFlightSuggestions.delete(inFlightKey);
  });
  inFlightSuggestions.set(inFlightKey, suggestionPromise);
  return suggestionPromise;
}
